import moment from "jalali-moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import endpoints from "../../config/endpoints";
import { Sidebar } from "../../layouts/Sidebar";
import { setLoading } from "../../services/reducers/homeSlice";
import restApi from "../../services/restApi";
import tools from "../../utils/tools";

// Bulk edits over every product of a category (with its subcategories), a
// brand or a tag: percentage price changes, undoable latest-first, and
// switching products active / inactive.

const targetTitles = {
  category: "دسته بندی",
  brand: "برند",
  tag: "تگ",
};

type TargetType = keyof typeof targetTitles;

const TargetPicker = ({
  value,
  onChange,
  options,
}: {
  value: { type: TargetType; id: any };
  onChange: (value: { type: TargetType; id: any }) => void;
  options: Record<TargetType, any[]>;
}) => {
  const list = tools.selectFormatter(options[value.type], "id", "title");
  return (
    <>
      <label className="sideBarTitle">انتخاب بر اساس</label>
      <select
        className="addInput"
        value={value.type}
        onChange={(input) =>
          onChange({ type: input.target.value as TargetType, id: null })
        }
      >
        {Object.entries(targetTitles).map(([key, title]) => (
          <option key={key} value={key}>
            {title}
          </option>
        ))}
      </select>
      <label className="sideBarTitle">{targetTitles[value.type]}</label>
      <Select
        className="width100"
        placeholder="انتخاب کنید"
        options={list}
        value={list.find((e) => e.value == value.id) || null}
        onChange={(selected: any) =>
          onChange({ ...value, id: selected?.value ?? null })
        }
      />
    </>
  );
};

const Bulk = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [options, setOptions] = useState<Record<TargetType, any[]>>({
    category: [],
    brand: [],
    tag: [],
  });
  const [history, setHistory] = useState<any[]>([]);
  const [priceTarget, setPriceTarget] = useState<{ type: TargetType; id: any }>({
    type: "category",
    id: null,
  });
  const [direction, setDirection] = useState<"up" | "down">("up");
  const [percent, setPercent] = useState("");
  const [statusTarget, setStatusTarget] = useState<{ type: TargetType; id: any }>({
    type: "category",
    id: null,
  });
  const [status, setStatus] = useState("1");

  const fail = (text?: string) =>
    Swal.fire({
      title: "ناموفق",
      text: text || "خطایی رخ داده",
      icon: "error",
      confirmButtonText: "متوجه شدم",
    });

  const targetName = (target: { type: TargetType; id: any }) =>
    options[target.type].find((e) => e.id == target.id)?.title;

  /** Asks the admin to confirm, showing how many products will change. */
  const confirmFor = async (
    target: { type: TargetType; id: any },
    describe: (count: number) => string,
  ) => {
    const res = await restApi(endpoints.bulk.count).get({
      targetType: target.type,
      targetId: target.id,
    });
    if (res.code != 200) {
      fail(res?.data);
      return false;
    }
    if (!res.data.productCount) {
      fail("محصولی در این انتخاب وجود ندارد");
      return false;
    }
    const answer = await Swal.fire({
      title: "تایید",
      text: describe(res.data.productCount),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "اعمال",
      cancelButtonText: "انصراف",
    });
    return answer.isConfirmed;
  };

  const fetchHistory = async () => {
    const res = await restApi(endpoints.bulk.price).get();
    if (res.code == 200) setHistory(res.data);
  };

  const fetchData = async () => {
    dispatch(setLoading(true));
    const res = await Promise.all([
      restApi(endpoints.category.index).get(),
      restApi(endpoints.brand.index).get(),
      restApi(endpoints.tag.index).get(),
      fetchHistory(),
    ]);
    setOptions({
      category: res[0]?.data || [],
      brand: res[1]?.data || [],
      tag: res[2]?.data || [],
    });
    dispatch(setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const applyPrice = async () => {
    const value = Number(tools.persianNumToEn(percent));
    if (!priceTarget.id) {
      fail(`${targetTitles[priceTarget.type]} را انتخاب کنید`);
      return;
    }
    if (!value || value <= 0) {
      fail("درصد را وارد کنید");
      return;
    }
    if (direction === "down" && value >= 100) {
      fail("کاهش باید کمتر از ۱۰۰ درصد باشد");
      return;
    }

    const verb = direction === "up" ? "افزایش" : "کاهش";
    const confirmed = await confirmFor(
      priceTarget,
      (count) =>
        `قیمت ${tools.engNumToPersian(count)} محصول از ${targetTitles[priceTarget.type]} «${targetName(priceTarget)}» ${tools.engNumToPersian(value)} درصد ${verb} می‌یابد.`,
    );
    if (!confirmed) return;

    dispatch(setLoading(true));
    const res = await restApi(endpoints.bulk.price).post({
      targetType: priceTarget.type,
      targetId: priceTarget.id,
      percent: direction === "up" ? value : -value,
    });
    dispatch(setLoading(false));

    if (res.code == 200) {
      Swal.fire({
        title: "موفق",
        text: `قیمت ${tools.engNumToPersian(res.data.productCount)} محصول تغییر کرد`,
        icon: "success",
        confirmButtonText: "متوجه شدم",
      });
      setPercent("");
      fetchHistory();
    } else {
      fail(res?.data);
    }
  };

  const revert = async (change: any) => {
    const answer = await Swal.fire({
      title: "بازگشت تغییر",
      text: `قیمت محصولات ${targetTitles[change.targetType]} «${change.targetTitle}» به قبل از این تغییر برمی‌گردد. قیمت محصولاتی که بعد از این تغییر دستی ویرایش شده‌اند، دست نمی‌خورد.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "بازگشت",
      cancelButtonText: "انصراف",
    });
    if (!answer.isConfirmed) return;

    dispatch(setLoading(true));
    const res = await restApi(endpoints.bulk.revert(change.id)).post({});
    dispatch(setLoading(false));

    if (res.code == 200) {
      const { restored, skipped } = res.data;
      Swal.fire({
        title: "موفق",
        text:
          `قیمت ${tools.engNumToPersian(restored)} مورد به حالت قبل برگشت.` +
          (skipped
            ? ` ${tools.engNumToPersian(skipped)} مورد به دلیل ویرایش دستی بعد از این تغییر، دست نخورد.`
            : ""),
        icon: "success",
        confirmButtonText: "متوجه شدم",
      });
      fetchHistory();
    } else {
      fail(res?.data);
    }
  };

  const applyStatus = async () => {
    if (!statusTarget.id) {
      fail(`${targetTitles[statusTarget.type]} را انتخاب کنید`);
      return;
    }

    const label = status == "1" ? "فعال (موجود)" : "غیر فعال (تماس بگیرید)";
    const confirmed = await confirmFor(
      statusTarget,
      (count) =>
        `وضعیت ${tools.engNumToPersian(count)} محصول از ${targetTitles[statusTarget.type]} «${targetName(statusTarget)}» ${label} می‌شود.`,
    );
    if (!confirmed) return;

    dispatch(setLoading(true));
    const res = await restApi(endpoints.bulk.status).post({
      targetType: statusTarget.type,
      targetId: statusTarget.id,
      status: Number(status),
    });
    dispatch(setLoading(false));

    if (res.code == 200) {
      Swal.fire({
        title: "موفق",
        text: res.data.productCount
          ? `وضعیت ${tools.engNumToPersian(res.data.productCount)} محصول تغییر کرد`
          : "همه محصولات از قبل در این وضعیت بودند",
        icon: "success",
        confirmButtonText: "متوجه شدم",
      });
    } else {
      fail(res?.data);
    }
  };

  return (
    <>
      <body className="dashboardBody">
        <Sidebar />
        <main className="dashBoardMain main">
          <div className="addInfoHeader">
            <span>
              <h1 className="sideBarTitle">بازگشت به صفحه محصولات</h1>
              <h1 className="dashBoardTitle">تغییر گروهی محصولات</h1>
            </span>
            <i className="backAdd clickable" onClick={() => navigate("/product")}></i>
          </div>

          <section className="bottomSection">
            <h1 className="dashBoardTitle">تغییر گروهی قیمت</h1>
            <form className="addInfo" onSubmit={(e) => e.preventDefault()}>
              <TargetPicker value={priceTarget} onChange={setPriceTarget} options={options} />
              <label className="sideBarTitle">نوع تغییر</label>
              <select
                className="addInput"
                value={direction}
                onChange={(input) => setDirection(input.target.value as "up" | "down")}
              >
                <option value="up">افزایش</option>
                <option value="down">کاهش</option>
              </select>
              <label className="sideBarTitle">درصد</label>
              <input
                className="addInput"
                inputMode="decimal"
                placeholder="مثلا ۱۰"
                value={percent}
                onChange={(input) => setPercent(input.target.value)}
              />
              <small dir="rtl">
                تغییر روی قیمت اصلی محصولات و شاخص‌ها اعمال می‌شود. قیمت با تخفیف هم به همان نسبت تغییر می‌کند تا درصد تخفیف هر محصول ثابت بماند. محصولات بدون قیمت (تماس بگیرید) تغییر نمی‌کنند.
              </small>
              <span className="dashboardHeader clickable" onClick={applyPrice}>
                اعمال تغییر قیمت
              </span>
            </form>
          </section>

          <section className="bottomSection">
            <h1 className="dashBoardTitle">تاریخچه تغییرات قیمت</h1>
            <small dir="rtl">فقط آخرین تغییر قابل بازگشت است؛ بعد از بازگشت آن، تغییر قبلی قابل بازگشت می‌شود.</small>
            <table>
              <thead>
                <tr className="dashTr1">
                  <th>عملیات</th>
                  <th>وضعیت</th>
                  <th>تاریخ</th>
                  <th>کاربر</th>
                  <th>تعداد محصول</th>
                  <th>درصد</th>
                  <th>انتخاب</th>
                  <th>ردیف</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr className="dashTr2">
                    <td colSpan={8}>تغییری ثبت نشده است</td>
                  </tr>
                )}
                {history.map((change, index) => (
                  <tr className="dashTr2" key={change.id}>
                    <td>
                      {change.canRevert && (
                        <span className="dashboardHeader clickable" onClick={() => revert(change)}>
                          بازگشت
                        </span>
                      )}
                    </td>
                    <td>
                      {change.revertedAt
                        ? `بازگشت داده شده (${moment(change.revertedAt).format("jYYYY/jMM/jDD HH:mm")})`
                        : "اعمال شده"}
                    </td>
                    <td>{moment(change.createdAt).format("jYYYY/jMM/jDD HH:mm")}</td>
                    <td>{change.admin || "-"}</td>
                    <td>{tools.engNumToPersian(change.productCount)}</td>
                    <td dir="ltr">
                      {tools.engNumToPersian(
                        `${change.percent > 0 ? "+" : ""}${change.percent}%`,
                      )}
                    </td>
                    <td>
                      {targetTitles[change.targetType]}: {change.targetTitle}
                    </td>
                    <td>{tools.engNumToPersian(index + 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="bottomSection">
            <h1 className="dashBoardTitle">تغییر گروهی وضعیت</h1>
            <form className="addInfo" onSubmit={(e) => e.preventDefault()}>
              <TargetPicker value={statusTarget} onChange={setStatusTarget} options={options} />
              <label className="sideBarTitle">وضعیت جدید</label>
              <select
                className="addInput"
                value={status}
                onChange={(input) => setStatus(input.target.value)}
              >
                <option value="1">فعال (موجود)</option>
                <option value="0">غیر فعال (تماس بگیرید)</option>
              </select>
              <span className="dashboardHeader clickable" onClick={applyStatus}>
                اعمال تغییر وضعیت
              </span>
            </form>
          </section>
        </main>
      </body>
    </>
  );
};

export default Bulk;
